// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next'; 

const AuthContext = createContext();

// Create and export the custom hook
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isSessionLoading, setIsSessionLoading] = useState(true);
    const { i18n } = useTranslation()
    const isAuthenticated = !!user;

    const login = (userData) => {
        setUser(userData);
        // Apply language immediately on login
        if (userData?.analysis_preferences?.default_language) {
            i18n.changeLanguage(userData.analysis_preferences.default_language);
        }
    };

    const logout = useCallback(async () => {
        try {
            await fetch('/api/auth/logout', { 
                method: 'POST',
                credentials: 'include'
            });
        } catch (err) {
            console.error("Logout API call failed, proceeding with client-side redirect.", err);
        } finally {
            // Redirect to the homepage, which will show the login page for unauthenticated users.
            window.location.href = '/';
        }
    }, []);

    // --- The Centralized API Handler ---
    const fetchWithAuth = useCallback(async (url, options = {}) => {
        // Ensure credentials are always included
        options.credentials = 'include';
        
        const response = await fetch(url, options);

        // Check for 401 Unauthorized status
        if (response.status === 401) {
            // Dispatch the global event so the UI can gracefully show the Session Expired modal
            // instead of violently redirecting the user to the landing page.
            window.dispatchEvent(new CustomEvent('session-expired'));

            // Throw an error to stop the promise chain in the original calling function.
            // This prevents the component from trying to process an invalid response.
            let errorData = { error: 'SESSION_EXPIRED', message: 'Your session has expired.' };
            try {
                errorData = await response.clone().json();
            } catch (e) { /* ignore if response is not JSON */ }
            
            throw new Error(errorData.message);
        }

        // If the response is not a 401, return it to be processed normally.
        return response;

    }, []);

    const updateUserSetting = async (settingKey, settingValue) => {
        try {
            // Use our new fetchWithAuth function
            const response = await fetchWithAuth('/api/user/settings', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [settingKey]: settingValue }),
            });
            const data = await response.json();
            if (response.ok) {
                setUser(prevUser => ({ ...prevUser, ...data.user }));
                return { success: true };
            } else {
                throw new Error(data.message || 'Failed to update setting.');
            }
        } catch (error) {
            console.error("Error updating setting:", error);
            return { success: false, error: error.message };
        }
    };

    useEffect(() => {
        const checkUserSession = async () => {
          try {
            // Use a STANDARD fetch here. A 401 is an expected state (not an error)
            // for an anonymous user on initial load.
            const response = await fetch('/api/user/me', {
                credentials: 'include' // Still need credentials to send the cookie
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              // Apply language on initial page load (session restore)
              if (userData?.analysis_preferences?.default_language) {
                  i18n.changeLanguage(userData.analysis_preferences.default_language);
              }
            } else {
              // If the response is not ok (e.g., 401), just set user to null.
              // This breaks the loop.
              setUser(null);
            }
          } catch (err) {
            console.error("Failed to check session:", err);
            setUser(null);
          } finally {
            setIsSessionLoading(false);
          }
        };
        checkUserSession();
    }, [i18n])

    // Expose the new function through the context
    const value = { user, isAuthenticated, isSessionLoading, login, logout, setUser, updateUserSetting, fetchWithAuth };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };